import type { Task } from "~/types/task";
import { isBeforeToday, isSameDay, isWithinCurrentWeek, parseToDate } from "~/utils/date";

/**
 * Filter tasks by time period (today, this week, or all)
 * TODO: I should probably move this to the date utils file
 */
export function filterTasksByTimePeriod(
  tasks: Task[],
  period: "today" | "this-week" | "all",
): Task[] {
  if (period === "all") {
    return tasks;
  }

  // Get today's date in local timezone as YYYY-MM-DD
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  if (period === "today") {
    return tasks.filter((task) => {
      // Convert UTC date to local timezone, then extract date
      const taskDate = new Date(task.end_date ? task.end_date : task.start_date);
      const taskDateStr = `${taskDate.getFullYear()}-${String(taskDate.getMonth() + 1).padStart(2, "0")}-${String(taskDate.getDate()).padStart(2, "0")}`;

      return taskDateStr === todayStr;
    });
  }

  if (period === "this-week") {
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
    const endOfWeekStr = `${endOfWeek.getFullYear()}-${String(endOfWeek.getMonth() + 1).padStart(2, "0")}-${String(endOfWeek.getDate()).padStart(2, "0")}`;

    return tasks.filter((task) => {
      // Convert UTC date to local timezone, then extract date
      const taskDate = new Date(task.end_date ? task.end_date : task.start_date);
      const taskDateStr = `${taskDate.getFullYear()}-${String(taskDate.getMonth() + 1).padStart(2, "0")}-${String(taskDate.getDate()).padStart(2, "0")}`;

      return taskDateStr >= todayStr && taskDateStr <= endOfWeekStr;
    });
  }

  return tasks;
}

export const DONE_STATUSES: Task["status"][] = ["completed", "approved"];

const RECENT_DONE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Drop done tasks (`completed`/`approved`) that are older than a rolling
 * 7-day window since `completed_at`. Non-done tasks always pass through.
 *
 * The overview keeps recent completions visible (progress satisfaction)
 * without the list filling up with stale done tasks. Done tasks without a
 * `completed_at` timestamp are dropped too — they predate the column and
 * cannot be proven recent.
 */
export function filterRecentDoneTasks(tasks: Task[]): Task[] {
  const cutoff = Date.now() - RECENT_DONE_WINDOW_MS;
  return tasks.filter((task) => {
    if (!DONE_STATUSES.includes(task.status)) {
      return true;
    }
    const completedAt = task.completed_at ? new Date(task.completed_at).getTime() : null;
    return completedAt !== null && completedAt >= cutoff;
  });
}

export interface DueDateGroup {
  key: "overdue" | "today" | "week" | "later" | "none";
  label: string;
  tasks: Task[];
}

/**
 * Group tasks into due-date buckets (overdue, today, this week, later, no due
 * date), sorted soonest-first within each bucket. Due date is `end_date`,
 * falling back to `start_date` — same convention as the dashboard summary.
 */
export function groupTasksByDueDate(tasks: Task[]): DueDateGroup[] {
  const groups: DueDateGroup[] = [
    { key: "overdue", label: "Overdue", tasks: [] },
    { key: "today", label: "Due Today", tasks: [] },
    { key: "week", label: "Due This Week", tasks: [] },
    { key: "later", label: "Later", tasks: [] },
    { key: "none", label: "No Due Date", tasks: [] },
  ];
  const byKey = new Map(groups.map((g) => [g.key, g]));

  tasks.forEach((task) => {
    const due = parseToDate(task.end_date ?? task.start_date);
    let key: DueDateGroup["key"] = "none";
    if (due) {
      if (isBeforeToday(due)) key = "overdue";
      else if (isSameDay(due, new Date())) key = "today";
      else if (isWithinCurrentWeek(due)) key = "week";
      else key = "later";
    }
    byKey.get(key)!.tasks.push(task);
  });

  groups.forEach((g) => {
    g.tasks.sort((a, b) => {
      const da = parseToDate(a.end_date ?? a.start_date)?.getTime() ?? Infinity;
      const db = parseToDate(b.end_date ?? b.start_date)?.getTime() ?? Infinity;
      return da - db;
    });
  });

  return groups.filter((g) => g.tasks.length > 0);
}

export function filterReviewTasksByTimePeriod(
  tasks: Task[],
  period: "today" | "this-week" | "all",
): Task[] {
  if (period === "all") {
    return tasks;
  }

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  if (period === "today") {
    return tasks.filter((task) => {
      const dateStr = task.review_submitted_at || task.updated_at;
      if (!dateStr) return false;

      const taskDate = new Date(dateStr);
      const taskDateStr = `${taskDate.getFullYear()}-${String(taskDate.getMonth() + 1).padStart(2, "0")}-${String(taskDate.getDate()).padStart(2, "0")}`;
      return taskDateStr === todayStr;
    });
  }

  if (period === "this-week") {
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
    const endOfWeekStr = `${endOfWeek.getFullYear()}-${String(endOfWeek.getMonth() + 1).padStart(2, "0")}-${String(endOfWeek.getDate()).padStart(2, "0")}`;

    return tasks.filter((task) => {
      const dateStr = task.review_submitted_at || task.updated_at;
      if (!dateStr) return false;

      const taskDate = new Date(dateStr);
      const taskDateStr = `${taskDate.getFullYear()}-${String(taskDate.getMonth() + 1).padStart(2, "0")}-${String(taskDate.getDate()).padStart(2, "0")}`;
      return taskDateStr >= todayStr && taskDateStr <= endOfWeekStr;
    });
  }

  return tasks;
}

/**
 * Darken a color while preserving its hue
 * @param color - Hex color string (e.g., "#c7d2fe")
 * @param amount - Amount to darken (0.0 to 1.0, default 0.2)
 * @returns RGBA color string with reduced lightness
 */
export function darkenColor(color: string, amount: number = 0.2): string {
  // Convert hex to RGB
  const hex = color.replace("#", "");
  let r = parseInt(hex.substring(0, 2), 16) / 255;
  let g = parseInt(hex.substring(2, 4), 16) / 255;
  let b = parseInt(hex.substring(4, 6), 16) / 255;

  // Convert RGB to HSL
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0,
    s = 0,
    l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  // Darken by reducing lightness only (preserves hue and saturation)
  l = Math.max(0, l * (1 - amount));

  // Convert HSL back to RGB
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, 0.5)`;
}

/**
 * Create a canvas pattern with diagonal stripes for Chart.js
 * @param color - Base hex color for the pattern
 * @param config - Optional configuration for stripe appearance
 * @returns Canvas pattern or null if window is not available
 */
export function createStripedPattern(
  color: string,
  config: {
    lineThickness?: number;
    lineSpacing?: number;
    lineAngle?: number;
  } = {},
): CanvasPattern | null {
  if (typeof window === "undefined") return null;

  const { lineThickness = 2, lineSpacing = 10, lineAngle = 45 } = config;

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  // Make canvas much larger to avoid visible repetition
  const canvasSize = 300;
  canvas.width = canvasSize;
  canvas.height = canvasSize;

  // Set background color
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, canvasSize, canvasSize);

  // Create diagonal stripes with a darker version of the color
  // Use 0.25 for 25% darker stripes (subtle but visible)
  ctx.strokeStyle = darkenColor(color, 0.25);
  ctx.lineWidth = lineThickness;
  ctx.beginPath();

  // Convert angle to radians
  const angleRad = (lineAngle * Math.PI) / 180;

  // Calculate line positions based on angle and spacing
  const diagonal = Math.sqrt(canvasSize * canvasSize + canvasSize * canvasSize);
  const spacing = lineSpacing;

  // Draw lines based on angle - cover entire large canvas
  if (lineAngle === 45) {
    // Optimized for 45-degree diagonal
    for (let i = -canvasSize; i <= canvasSize * 2; i += spacing) {
      ctx.moveTo(i, 0);
      ctx.lineTo(i + canvasSize, canvasSize);
    }
  } else if (lineAngle === 0) {
    // Horizontal lines
    for (let y = 0; y <= canvasSize; y += spacing) {
      ctx.moveTo(0, y);
      ctx.lineTo(canvasSize, y);
    }
  } else if (lineAngle === 90) {
    // Vertical lines
    for (let x = 0; x <= canvasSize; x += spacing) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvasSize);
    }
  } else {
    // General angle calculation
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);

    // Calculate how many lines we need to cover the canvas
    const numLines = Math.ceil(diagonal / spacing) + 2;

    for (let i = -numLines; i <= numLines; i++) {
      const offset = i * spacing;

      // Calculate start and end points for the line
      const startX = -diagonal + offset * cos;
      const startY = -diagonal + offset * sin;
      const endX = diagonal + offset * cos;
      const endY = diagonal + offset * sin;

      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
    }
  }

  ctx.stroke();
  return ctx.createPattern(canvas, "repeat");
}
